import { type JSX, useContext } from 'react';
import { generatePath } from 'react-router';

import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACContext, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { CircleIndicator } from 'Components/Indicator/CircleIndicator';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { RISK_DETAILS } from 'Config/PathTemplates';
import { undefinedComparator } from 'Helpers/Compare';
import { RISK_ROLES } from 'Helpers/UserUtils';
import { TreatmentPlanStatus } from 'Models/Actions';
import { getEffectivenessVariantColor, numberAsEffectiveness } from 'Models/OperationalControls';
import { RiskHistory, RiskResponse, formatAverageWeightedEffectiveness } from 'Models/RiskRegister';

import styles from './RiskTable.module.css';

interface BaseProps {
    openLinksInNewTabs?: boolean;

    /**
     * If the table is being displayed for a specific control, this is the identifier of the control. The Control Environment column will be extended to include the control's weight for each mitigated Risk.
     */
    controlContext?: string;
}

interface CurrentRisksProps extends BaseProps {
    type: 'current risks';
    risks: RiskResponse[];
}

interface AssessmentRisksProps extends BaseProps {
    type: 'assessment risks';
    riskHistories: RiskHistory[];
    onAssessmentRiskSelected: (riskHistory: RiskHistory) => void;
}

export type RiskTableProps = CurrentRisksProps | AssessmentRisksProps;

type RiskItem = RiskResponse | RiskHistory;

export const RiskTable = (props: RiskTableProps): JSX.Element => {
    const cachedData = useCachedData();
    const rbacContext = useContext(RBACContext);
    const items: RiskItem[] = props.type === 'current risks' ? props.risks : props.riskHistories;

    const userHasRiskDetailsAccess = userHasAuthorizedRole(rbacContext!.roles, RISK_ROLES);

    const getControlWeight = (risk: RiskItem): number | undefined => {
        if (props.controlContext && props.type === 'current risks') {
            const controlMapping = (risk as RiskResponse).controls.find((controlMapping) => controlMapping.control.identifier === props.controlContext);
            return controlMapping?.weight;
        }
        return undefined;
    };

    const getCurrentRiskLinkTo = (risk: RiskResponse): string => generatePath(RISK_DETAILS, { risk_id: risk.id });

    /**
     * If the RiskTable is showing current Risks, the titles link to the Risk details page.
     * If the RiskTable is showing Risks from a Risk Review, the titles are buttons that pop a modal populated with the RiskHistory.
     */
    const getRiskTitleContent = (risk: RiskItem): JSX.Element => {
        if (props.type === 'current risks') {
            return (
                <Link size="sm" to={getCurrentRiskLinkTo(risk as RiskResponse)} openInNewTab={props.openLinksInNewTabs}>
                    {risk.title}
                </Link>
            );
        }

        return (
            <Button size="sm" variant="linkText" onClick={() => props.onAssessmentRiskSelected(risk as RiskHistory)}>
                {risk.title}
            </Button>
        );
    };

    const columns: ColumnConfig<RiskItem>[] = [
        {
            type: ColumnType.CUSTOM,
            name: 'Risk',
            getValue: (risk) => risk.title,
            getTableCellContent: (risk) => (
                <>
                    <div className={styles.riskNameContainer}>{userHasRiskDetailsAccess ? getRiskTitleContent(risk) : <Text noStyles>{risk.title}</Text>}</div>
                    <Text color="darkGray" variant="Text3" noStyles>
                        {risk.category.title}
                    </Text>
                </>
            ),
            sortComparator: (a, b) => a.localeCompare(b),
        },
        {
            type: ColumnType.DECIMAL,
            name: 'Inherent Risk',
            getNumber: (risk) => risk.total_inherent_risk,
            numberOfDigitsAfterDecimalPoint: 2,
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Control Environment',
            getValue: (risk) => risk.control_environment_effectiveness,
            getTableCellContent: (risk) => {
                const controlWeight = getControlWeight(risk);
                return (
                    <div className={styles.controlEnvironmentContainer}>
                        <div className={styles.controlEnvironmentEffectivenessContainer}>
                            <CircleIndicator variant={getEffectivenessVariantColor(numberAsEffectiveness(risk.control_environment_effectiveness))} />
                            <Text noStyles>{formatAverageWeightedEffectiveness(risk.control_environment_effectiveness)}</Text>
                        </div>
                        {controlWeight !== undefined && <Text noStyles>{`${controlWeight}%`}</Text>}
                    </div>
                );
            },
            sortComparator: (a, b) => undefinedComparator(a, b, (va, vb) => (va > vb ? 1 : -1)),
        },
        {
            type: ColumnType.DECIMAL,
            name: 'Current Residual Risk',
            getNumber: (risk) => risk.total_current_risk,
            numberOfDigitsAfterDecimalPoint: 2,
        },
        {
            type: ColumnType.STRING,
            name: 'Treatment Plan',
            getString: (risk) => (risk.treatment_plan_status !== TreatmentPlanStatus.MISSING ? risk.treatment_plan_status : undefined),
        },
        {
            type: ColumnType.DECIMAL,
            name: 'Target Residual Risk',
            getNumber: (risk) => risk.total_target_risk,
            numberOfDigitsAfterDecimalPoint: 2,
        },
        {
            type: ColumnType.STRING,
            name: 'Strategy',
            getString: (risk) => risk.strategy?.title,
        },
    ];

    if (props.type === 'current risks') {
        columns.push({
            type: ColumnType.MULTIPLE_TAGS,
            name: 'Tags',
            getTagIds: (risk) => (risk as RiskResponse).tags,
            convertTagIdsToDisplayableTags: cachedData.convertTagIdsToDisplayableTags,
        });
    } else {
        columns.push({
            type: ColumnType.MULTIPLE_HISTORICAL_TAGS,
            name: 'Tags',
            getTags: (risk) => (risk as RiskHistory).tags,
        });
    }

    return <Table columns={columns} defaultSortColumn="Risk" filters={[]} getUniqueIdForRow={(risk) => risk.id} rowsData={items} />;
};
